import React, { useState, useEffect, ReactNode } from 'react';
import { AuthContext, AuthContextType, User } from '../contexts/AuthContext.tsx';
import { http } from '../lib/http';

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const hasVerifiedRef = React.useRef(false);
  let justLoggedOut = false;
  let inflightVerify: Promise<any> | null = null;

  useEffect(() => {
    if (hasVerifiedRef.current) return;
    hasVerifiedRef.current = true;
    verifyAuthentication();
  }, []);

  const verifyAuthentication = async () => {
    if (inflightVerify) {
      console.log('[Auth] Verify already in flight, returning existing promise');
      return inflightVerify;
    }

    if (justLoggedOut) {
      // Skip one verify cycle to avoid a race while the browser applies Set-Cookie deletions.
      justLoggedOut = false;
      setLoading(false);
      return;
    }

    // If suppress flag present, skip any auto-OAuth
    const suppress = document.cookie.includes('cad_no_silent_oauth=1');

    inflightVerify = (async () => {
      try {
        console.log('[Auth] Checking authentication...');
        
        // 1) Try cookie-based auth first (httpOnly cookie travels automatically)
        const response = await http.post('/auth/verify-cookie');
        console.log('[Auth] Verify response:', response.data);
        
        if (response.data.success) {
          setUser(response.data.data.user);
          console.log('[Auth] Authentication verified, user:', response.data.data.user.username);
          
          // Clear legacy token if it exists
          const legacyToken = localStorage.getItem('cad_token');
          if (legacyToken) {
            console.log('[Auth] Migrating from localStorage to cookie-based auth');
            localStorage.removeItem('cad_token');
            delete http.defaults.headers.common['Authorization'];
          }
        } else {
          console.log('[Auth] Authentication verification failed:', response.data.error);
          clearAuthData();
        }
      } catch (error: any) {
        console.error('[Auth] Authentication verification error:', error);
        
        // Handle rate limiting (429) - back off and don't clear auth
        if (error.response?.status === 429) {
          console.log('[Auth] Rate limited during verification, backing off...');
          await new Promise(r => setTimeout(r, 3000)); // simple backoff
          setLoading(false);
          return;
        }
        
        // If it's a network error, don't clear auth - user might be offline
        if (error.code === 'NETWORK_ERROR' || error.message?.includes('Network Error')) {
          console.log('[Auth] Network error during verification, will retry later');
          setLoading(false);
          return;
        }
        
        // If cookie didn't verify (401/403), try legacy token once (only if not suppressed)
        if (!suppress) {
          try {
            const legacyToken = localStorage.getItem('cad_token');
            if (legacyToken) {
              console.log('[Auth] Cookie verify failed; trying legacy token /auth/verify');
              const legacyRes = await http.post('/auth/verify', { token: legacyToken });
              if (legacyRes.data?.success) {
                setUser(legacyRes.data.data.user);
                // Move off legacy token
                localStorage.removeItem('cad_token');
                delete http.defaults.headers.common['Authorization'];
                return;
              }
            }
            clearAuthData();
          } catch {
            clearAuthData();
          }
        } else {
          console.log('[Auth] Legacy token attempt suppressed by cad_no_silent_oauth');
          clearAuthData();
        }
      } finally {
        setLoading(false);
        inflightVerify = null;
      }
    })();
    
    return inflightVerify;
  };

  const clearAuthData = () => {
    // Clear any cached data
    localStorage.removeItem('cad_token'); // Legacy
    localStorage.removeItem('cad_shift_data');
    localStorage.removeItem('claimed_unit_id');
    delete http.defaults.headers.common['Authorization'];
  };

  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      const response = await http.post('/auth/login', {
        username,
        password,
      });

      if (response.data.success) {
        const { user: userData } = response.data.data;
        setUser(userData);
        console.log('[Auth] Login successful, user:', userData.username);
        
        // Token is now set in httpOnly cookie by server
        // No need to store in localStorage
        return true;
      }
      return false;
    } catch (error) {
      console.error('[Auth] Login error:', error);
      return false;
    }
  };

  const logout = async (options?: { skipServerRequest?: boolean }) => {
    const skipServerRequest = options?.skipServerRequest ?? false;
    try {
      if (!skipServerRequest) {
        await http.post('/auth/logout');     // cookies cleared on server
      }
    } catch (error) {
      console.error('[Auth] Logout error:', error);
    } finally {
      justLoggedOut = true;                // prevent immediate re-verify
      // Clear all local data
      clearAuthData();
      localStorage.removeItem('cad_shift_data'); // Clear shift data on logout
      setUser(null);
      console.log('[Auth] Logged out successfully');
    }
  };

  const value: AuthContextType = {
    user,
    login,
    logout,
    isAuthenticated: !!user,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
